import DOMPurify from 'dompurify';

// This is a rendering/editor boundary, not a migration of canonical manuscript data.
const TAGS = ['p', 'br', 'span', 'nt', 'a', 'img', 'strong', 'b', 'em', 'i', 'u', 's',
  'del', 'ins', 'sup', 'sub', 'mark', 'blockquote', 'pre', 'code', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'div', 'section',
  'article', 'aside', 'header', 'footer', 'nav', 'main', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
  'dl', 'dt', 'dd', 'ruby', 'rt', 'rp'];
const ATTRIBUTES = ['id', 'class', 'style', 'dir', 'lang', 'xml:lang', 'epub:type',
  'title', 'alt', 'colspan', 'rowspan', 'span', 'start', 'type', 'width', 'height',
  'href', 'src', 'role'];
const STYLE_PROPERTIES = new Set([
  'background-color', 'border', 'border-bottom', 'border-color', 'border-left',
  'border-right', 'border-style', 'border-top', 'border-width', 'border-collapse',
  'color', 'font-family', 'font-size', 'font-style', 'font-weight', 'letter-spacing',
  'line-height', 'list-style-type', 'margin', 'margin-bottom', 'margin-left',
  'margin-right', 'margin-top', 'padding', 'padding-bottom', 'padding-left',
  'padding-right', 'padding-top', 'text-align', 'text-decoration', 'text-indent',
  'text-transform', 'vertical-align', 'white-space', 'word-break', 'writing-mode',
  'page-break-before', 'page-break-after', 'page-break-inside',
  'break-before', 'break-after', 'break-inside', 'widows', 'orphans',
  'width', 'height', 'max-width', 'max-height',
]);
const UNSAFE_VALUE = /url\s*\(|@|expression\s*\(|(?:java|vb)script\s*:|data\s*:|behavior|-moz-binding|var\s*\(|attr\s*\(|\\|[<>]/i;
const hasControls = (value: string, allowWhitespace = false): boolean => Array.from(value).some(char =>
  (char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) && !(allowWhitespace && /[\t\n\r]/.test(char)));

export function sanitizeStyleDeclarations(value: string): string {
  if (typeof document === 'undefined' || value.length > 20_000) return '';
  const style = document.createElement('span').style;
  // Reject escapes/comments before CSSOM normalization can disguise their source.
  if (/\\|\/\*|[<>]/.test(value) || hasControls(value, true)) return '';
  style.cssText = value;
  const declarations: string[] = [];
  for (let index = 0; index < style.length; index += 1) {
    const property = style.item(index).toLowerCase();
    const cssValue = style.getPropertyValue(property).trim();
    if (!STYLE_PROPERTIES.has(property) || !cssValue || cssValue.length > 200
      || UNSAFE_VALUE.test(cssValue) || hasControls(cssValue) || !/^[\w\s#(),.%+'"/-]*$/u.test(cssValue)) continue;
    // Imported layout must remain inside the editor; extreme sizes/negative margins can cover controls.
    if (/^(margin|padding|width|height|max-width|max-height|font-size|line-height|letter-spacing|text-indent)/.test(property)
      && (/(?:^|[\s(])-[\d.]/.test(cssValue)
        || (cssValue.match(/\d+(?:\.\d+)?/g) || []).some(number => Number(number) > 1000))) continue;
    declarations.push(`${property}: ${cssValue}`);
  }
  // Match CSSOM/Quill serialization so controlled editors do not rewrite styles on every render.
  return declarations.length ? `${declarations.join('; ')};` : '';
}

function safeImageSource(value: string): boolean {
  if (/^data:image\/(?:png|jpeg|gif|webp|avif);base64,[a-z0-9+/=\s]+$/i.test(value)) return true;
  // Archive-relative image references survive editor/export round trips, but may not select another origin.
  if (/^(?!\/|\\)(?!.*(?:\\|\s))[^:?#]+$/u.test(value) && !hasControls(value)) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password && !url.port
      && (url.hostname === 'storage.googleapis.com' || url.hostname === 'firebasestorage.googleapis.com'
        || /^[a-z0-9][a-z0-9._-]*\.storage\.googleapis\.com$/i.test(url.hostname));
  } catch { return false; }
}

function safeLink(value: string): boolean {
  if (/^#[^\s<>]*$/.test(value)) return true;
  try {
    const url = new URL(value);
    return ['https:', 'mailto:'].includes(url.protocol) && !url.username && !url.password;
  } catch { return false; }
}

export function sanitizeRichTextHtml(value: string | null | undefined): string {
  if (typeof document === 'undefined') return ''; // No unsanitized SSR fallback.
  // Recover the existing manuscript contract for an unclosed imported style block:
  // remove its CSS prefix, then pass the remaining markup through the full sanitizer.
  const manuscript = String(value || '')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<style\b[^>]*>[^<]*(?=<|$)/gi, '');
  const fragment = DOMPurify.sanitize(manuscript, {
    ALLOWED_TAGS: TAGS,
    ALLOWED_ATTR: ATTRIBUTES,
    ADD_TAGS: ['nt'],
    ALLOW_DATA_ATTR: true,
    ALLOW_ARIA_ATTR: true,
    FORBID_TAGS: ['svg', 'math', 'template', 'iframe', 'object', 'embed', 'form', 'style', 'script'],
    FORBID_ATTR: ['name', 'srcset', 'action', 'formaction', 'xlink:href', 'contenteditable'],
    RETURN_DOM_FRAGMENT: true,
  }) as DocumentFragment;
  fragment.querySelectorAll<HTMLElement>('*').forEach(element => {
    const style = element.getAttribute('style');
    if (style !== null) {
      const safeStyle = sanitizeStyleDeclarations(style);
      if (safeStyle) element.setAttribute('style', safeStyle);
      else element.removeAttribute('style');
    }
    if (element.hasAttribute('src') && (element.tagName !== 'IMG'
      || !safeImageSource(element.getAttribute('src') || ''))) element.removeAttribute('src');
    if (element.hasAttribute('href') && (element.tagName !== 'A'
      || !safeLink(element.getAttribute('href') || ''))) element.removeAttribute('href');
    if (element.tagName === 'A') element.setAttribute('rel', 'noopener noreferrer');
    if (element.tagName === 'IMG') element.setAttribute('referrerpolicy', 'no-referrer');
  });
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}

/** Only a deliberately small, flat selector/declaration grammar is accepted. */
export function sanitizeStylesheet(value: string | null | undefined, scope: string): string {
  if (!/^[a-zA-Z0-9_-]+$/.test(scope)) return '';
  const input = String(value || '');
  if (input.length > 100_000 || /[\\@<>]|\/\*/.test(input)) return '';
  const blocks: string[] = [];
  let remainder = input;
  while (remainder.trim()) {
    const rule = /^\s*([^{}]+)\{([^{}]*)\}/.exec(remainder);
    if (!rule) return ''; // Reject nested/malformed CSS rather than extract fragments from it.
    remainder = remainder.slice(rule[0].length);
    const declarations = sanitizeStyleDeclarations(rule[2]);
    const selectors = rule[1].split(',').map(selector => selector.trim());
    if (!declarations || selectors.some(selector => !/^[a-zA-Z0-9_.#-][a-zA-Z0-9_.#\-\s>+~:]*$/.test(selector)
      || /:root|:has|:host|(?:^|[\s>+~])(?:html|body)(?:$|[\s>+~.:#])/i.test(selector))) continue;
    blocks.push(selectors.map(selector => `[data-manuscript-scope="${scope}"] ${selector}`).join(', ')
      + ` { ${declarations} }`);
  }
  return blocks.join('\n');
}

export const sanitizedHtml = (value: string | null | undefined): { __html: string } => ({
  __html: sanitizeRichTextHtml(value),
});
